import { useState } from "react";
import { MessageCircle, Phone, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ServiceAreaMap } from "../components/ServiceAreaMap";
import { useLang } from "../context/LanguageContext";
import { api, waLink, formatApiErrorDetail, WHATSAPP_NUMBER } from "../lib/api";
import { toast } from "sonner";

const faqs = {
  en: [
    { q: "Which areas do you cover?", a: "We cover the entire Lembah Klang (Klang Valley) — Kuala Lumpur, Petaling Jaya, Shah Alam, Klang, Subang Jaya, Ampang, Cheras, Puchong, Kajang, Putrajaya, Gombak and Rawang." },
    { q: "How quickly can a doctor visit?", a: "For most areas, same-day visits are available if booked before noon. Urgent cases can be arranged via WhatsApp for faster response." },
    { q: "How do I pay for the service?", a: "Payment is made after the service is completed, at your home. We accept cash, bank transfer and e-wallets. No advance payment is required." },
    { q: "Are your doctors certified?", a: "Yes, all our doctors are registered with the Malaysian Medical Council (MMC) and have verified qualifications and years of clinical experience." },
    { q: "What should I prepare before the visit?", a: "Have your identification, any current medications, and previous medical reports ready. Ensure a clean, well-lit space for the doctor to work." },
    { q: "Can I choose a specific doctor?", a: "Yes! During booking you can select a preferred doctor, subject to their availability. Otherwise, we assign the best available doctor for your needs." },
  ],
  ms: [
    { q: "Kawasan mana yang anda liputi?", a: "Kami meliputi seluruh Lembah Klang — Kuala Lumpur, Petaling Jaya, Shah Alam, Klang, Subang Jaya, Ampang, Cheras, Puchong, Kajang, Putrajaya, Gombak dan Rawang." },
    { q: "Berapa cepat doktor boleh melawat?", a: "Untuk kebanyakan kawasan, lawatan hari sama tersedia jika ditempah sebelum tengah hari. Kes segera boleh diatur melalui WhatsApp untuk tindak balas lebih pantas." },
    { q: "Bagaimana cara pembayaran?", a: "Pembayaran dibuat selepas perkhidmatan selesai, di rumah anda. Kami menerima tunai, pindahan bank dan e-dompet. Tiada bayaran pendahuluan diperlukan." },
    { q: "Adakah doktor anda bertauliah?", a: "Ya, semua doktor kami berdaftar dengan Majlis Perubatan Malaysia (MMC) dan mempunyai kelayakan yang disahkan serta pengalaman klinikal bertahun-tahun." },
    { q: "Apa yang perlu disediakan sebelum lawatan?", a: "Sediakan kad pengenalan, ubat-ubatan semasa, dan laporan perubatan terdahulu. Pastikan ruang yang bersih dan terang untuk doktor bekerja." },
    { q: "Bolehkah saya memilih doktor tertentu?", a: "Ya! Semasa tempahan anda boleh memilih doktor pilihan, tertakluk kepada ketersediaan. Jika tidak, kami akan menetapkan doktor terbaik untuk keperluan anda." },
  ],
};

export default function Contact() {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(lang === "ms" ? "Mesej dihantar! Kami akan menghubungi anda." : "Message sent! We'll get back to you.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">{t("contact.title")}</h1>
      <p className="text-base md:text-lg text-slate-500 mb-12 max-w-2xl">{t("contact.subtitle")}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-8 space-y-5" data-testid="contact-form">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{t("contact.form_title")}</h2>
          <div>
            <Label htmlFor="c-name">{t("contact.name")}</Label>
            <Input id="c-name" required data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="c-email">{t("contact.email")}</Label>
              <Input id="c-email" type="email" required data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="c-phone">{t("contact.phone")}</Label>
              <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-msg">{t("contact.message")}</Label>
            <Textarea id="c-msg" required rows={4} data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2" />
          </div>
          <Button type="submit" disabled={sending} data-testid="contact-submit-btn" className="rounded-full px-8">
            {sending ? "..." : t("contact.send")}
          </Button>
        </form>

        <div className="space-y-6">
          <a href={waLink(lang === "ms" ? "Hai, saya ada pertanyaan tentang perkhidmatan anda." : "Hi, I have a question about your services.")}
            target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card"
            className="flex items-center gap-4 bg-[#25D366] text-white rounded-2xl p-6 hover:scale-[1.02] transition-transform">
            <MessageCircle className="w-8 h-8" />
            <div>
              <p className="font-semibold">WhatsApp</p>
              <p className="text-sm text-emerald-50">+{WHATSAPP_NUMBER}</p>
            </div>
          </a>
          <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-6">
            <Phone className="w-8 h-8 text-primary" />
            <div>
              <p className="font-semibold text-slate-900">{lang === "ms" ? "Telefon" : "Phone"}</p>
              <p className="text-sm text-slate-500">+{WHATSAPP_NUMBER}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-6">
            <MapPin className="w-8 h-8 text-primary" />
            <div>
              <p className="font-semibold text-slate-900">{lang === "ms" ? "Kawasan" : "Coverage"}</p>
              <p className="text-sm text-slate-500">Lembah Klang, Malaysia</p>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-8">{t("contact.faq_title")}</h2>
      <Accordion type="single" collapsible className="bg-white rounded-2xl border border-slate-200 px-6 mb-20" data-testid="faq-accordion">
        {faqs[lang].map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`}>
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-medium">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-500 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-8">{t("map.title")}</h2>
      <ServiceAreaMap />
    </div>
  );
}
